import posthog from "posthog-js";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { CommandPaletteContext } from "./Context";
import { useCommandPaletteShortcut } from "../../hooks/commandPalette/useCommandPaletteShortcut";
import { useAuth } from "../auth/Context";

const LazyCommandPalette = lazy(() =>
  import("../../components/commandPalette/CommandPalette").then((m) => ({
    default: m.CommandPalette,
  })),
);

interface CommandPaletteProviderProps {
  children: ReactNode;
}

const noop = () => {};

export const CommandPaletteProvider = ({
  children,
}: CommandPaletteProviderProps) => {
  const { authenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  const open = useCallback(() => {
    setIsOpen(true);
    setHasBeenOpened(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      if (!prev) setHasBeenOpened(true);
      return !prev;
    });
  }, []);

  useCommandPaletteShortcut(authenticated ? toggle : noop);

  useEffect(() => {
    if (!authenticated) setIsOpen(false);
  }, [authenticated]);

  useEffect(() => {
    if (isOpen) posthog.capture("command_palette_opened");
  }, [isOpen]);

  const value = useMemo(
    () => ({ isOpen, open, close, toggle }),
    [isOpen, open, close, toggle],
  );

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
      {authenticated && hasBeenOpened && (
        <Suspense fallback={null}>
          <LazyCommandPalette isOpen={isOpen} onClose={close} />
        </Suspense>
      )}
    </CommandPaletteContext.Provider>
  );
};
